import React, { useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { FormFieldKeys, MultiGroupItemList, useChangeHandlers, useEntityLists, useFilterToggler, useOfferFiltersContext } from './OfferFilters.hooks'
import { muiTheme } from 'cityads-v3/utils/styleUtils';
import { ThemeProvider as MuiThemeProvider } from '@mui/material';
import classes from './OfferFilters.scss'

import Button from '@mui/material/Button'
import FilterTextField from 'cityads-v3/components/Forms/FilterTextField'
import { MultiGroup } from 'cityads-v3/components/Forms/FilterVertical'
import OfferFilterCheckbox from 'cityads-v3/components/Filter/OfferFilterCheckbox'
import Paper from '@mui/material/Paper'
import SearchIcon from '@mui/icons-material/SearchOutlined'
import { usePressEnter } from 'cityads-v3/hooks/utils'
import { useSelector } from 'react-redux'
import { RootState } from 'cityads-v3/types/reducer'
import FilterCount from './FilterCount'

export type OfferFiltersProps = {
  embeddedUrlAdditional?: string
  displayFilters?: {
    [key in FormFieldKeys]: boolean
  }
}

const OfferFilters = function ({ displayFilters }: OfferFiltersProps): JSX.Element {
  const { t } = useTranslation(['Offers', 'main'])
  const user = useSelector((state: RootState) => state.auth.user);
  const isMobile = useSelector((state: RootState) => state.app.isMobile);
  const filterEl = useRef<HTMLElement>(null)

  const entityList = useEntityLists()
  const { showAll, handleToggleShowAll } = useFilterToggler()
  const { selectedFilters } = useOfferFiltersContext()
  const handleInputPressEnter = usePressEnter(selectedFilters.handleSubmit)

  const {
    handleCheckboxChange,
    handleMultiGroupChange,
    handleTagsChange,
    handleTextChange
  } = useChangeHandlers({
    filterEl,
    submit: selectedFilters.handleSubmit
  })

  const handleToggleExpand = useCallback(() => {
    setTimeout(() => {
      window?.dispatchEvent(new Event('resize'))
    }, 0)
  }, [])

  return (
    <>
      {displayFilters?.name !== false && (
        <FilterTextField
          placeholder={t('filters.nameHint')}
          icon={<SearchIcon sx={theme => ({ color: theme.palette.text.primary })} />}
          onChange={handleTextChange(selectedFilters.fields.name)}
          onKeyPress={handleInputPressEnter}
          value={selectedFilters.fields.name.value}

        />
      )}

      <Paper
        classes={{ root: classes.filterList }}
        elevation={isMobile ? 0 : 2}
        square={isMobile}
      >
        {showAll ? (
          <>

            <MuiThemeProvider theme={muiTheme}>
              {displayFilters?.tags !== false && entityList.tags.filter(tag => tag.isVisible).map(tag => (
                <OfferFilterCheckbox
                  {...tag}
                  key={tag.code}
                  checked={selectedFilters.fields.tags.value.includes(tag.code)}
                  onToggle={handleTagsChange(selectedFilters.fields.tags, tag.code)}
                />
              ))}
            </MuiThemeProvider>

            {displayFilters?.region_id !== false && (
              <MultiGroup
                expanded
                topLevelExpanded
                search
                searchLabel={t('filters.region_id_search')}
                name="region_id"
                title={t('filters.region_id')}
                items={entityList.regionList[0] || []}
                onToggleExpand={handleToggleExpand}
                onChange={handleMultiGroupChange<number>(selectedFilters.fields.region_id, entityList.regionList[0])}
                value={selectedFilters.fields.region_id.value}
              />
            )}

            {displayFilters?.direction_and_platform !== false && (
              <MultiGroup
                expanded
                name="direction_and_platform"
                title={t('filters.offer_type')}
                items={entityList.directionAndPlatformList}
                onToggleExpand={handleToggleExpand}
                onChange={handleMultiGroupChange<string>(selectedFilters.fields.direction_and_platform, entityList.directionAndPlatformList)}
                value={selectedFilters.fields.direction_and_platform.value}
              />
            )}

            {user && displayFilters?.subscription_status !== false ? (
              <MultiGroup
                name="subscription_status"
                title={t('filters.subscription_status')}
                items={entityList.subscriptionStatusList[0]?.subscriptionStatus.map(st => ({ title: st.name, value: st.id }))}
                onToggleExpand={handleToggleExpand}
                onChange={handleMultiGroupChange(selectedFilters.fields.subscription_status)}
                value={selectedFilters.fields.subscription_status.value}
              />
            ) : null}

            {displayFilters?.category_id !== false && (
              <MultiGroup
                expanded
                search
                searchLabel={t('filters.category_id_search')}
                name="category_id"
                title={t('filters.category_id')}
                items={entityList.categoryList[0] || []}
                onToggleExpand={handleToggleExpand}
                onChange={handleMultiGroupChange(selectedFilters.fields.category_id, entityList.categoryList[0] as unknown as MultiGroupItemList<number>)}
                value={selectedFilters.fields.category_id.value}
              />
            )}

            {displayFilters?.traffic_type_id !== false && (
              <MultiGroup
                name="traffic_type_id"
                title={t('filters.traffic_type_id')}
                search={true}
                searchLabel={t('filters.traffic_type_id_search')}
                searchIcon="edit_attributes"
                items={entityList.trafficTypes[0]?.map(tt => ({ title: tt.name, value: tt.id }))}
                onToggleExpand={handleToggleExpand}
                onChange={handleMultiGroupChange(selectedFilters.fields.traffic_type_id)}
                value={selectedFilters.fields.traffic_type_id.value}
              />
            )}

            {displayFilters?.tool_id !== false && (
              <MultiGroup
                name="tool_id"
                title={t('filters.tool_id')}
                search={true}
                searchLabel={t('filters.tool_id_search')}
                searchIcon="settings_applications"
                items={entityList.tools[0]?.tool?.map(tool => ({ title: tool.name, value: tool.id }))}
                onToggleExpand={handleToggleExpand}
                onChange={handleMultiGroupChange(selectedFilters.fields.tool_id)}
                value={selectedFilters.fields.tool_id.value}
              />
            )}

            {displayFilters?.has_promocode_attribution !== false && (
              <OfferFilterCheckbox
                title={t('filters.hasPromocodeAttribution')}
                checked={selectedFilters.fields.has_promocode_attribution.value}
                onToggle={handleCheckboxChange(selectedFilters.fields.has_promocode_attribution)}
              />
            )}

            {displayFilters?.is_deeplink_enabled !== false && (
              <OfferFilterCheckbox
                title={t('filters.isDeeplinkEnabled')}
                checked={selectedFilters.fields.is_deeplink_enabled.value}
                onToggle={handleCheckboxChange(selectedFilters.fields.is_deeplink_enabled)}
              />
            )}

            {displayFilters?.has_cart !== false && (
              <OfferFilterCheckbox
                title={t('filters.hasCart')}
                checked={selectedFilters.fields.has_cart.value}
                onToggle={handleCheckboxChange(selectedFilters.fields.has_cart)}
              />
            )}
          </>
        ) : null}

        <div className={classes.actionButtons}>
          <Button
            color="primary"
            onClick={selectedFilters.handleSubmit}
            variant="contained"
          >
            {t('main:buttons.apply')}
          </Button>

          {isMobile ? (
            <Button
              onClick={handleToggleShowAll}
            >
              {showAll ? t('hideFilters') : t('showFilters')}
            </Button>
          ) : null}
        </div>
      </Paper>

      <FilterCount
        filterEl={filterEl}
        onApply={selectedFilters.handleSubmit}
      />
    </>
  )
}

export default OfferFilters
